import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Staff-only console to create + manage member accounts. Reached at /admin.
export default function AdminPanel() {
  const { principal, isStaff, loading, staffLogin, logout } = useAuth();

  if (loading) {
    return <CenterMsg text="Loading…" />;
  }
  if (!isStaff) {
    return <StaffLogin onLogin={staffLogin} />;
  }
  return <MemberAdmin principal={principal} onLogout={logout} />;
}

function StaffLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onLogin(email.trim(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-iw-grid" style={{ background: "var(--iw-bg)" }}>
      <form onSubmit={submit} className="iw-card p-8 w-full max-w-md" data-testid="staff-login-form">
        <div className="iw-mono uppercase mb-1.5" style={{ color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }}>
          Wellness Team
        </div>
        <h1 className="serif font-bold m-0 mb-5" style={{ color: "var(--iw-primary)", fontSize: "26px" }}>
          Staff console
        </h1>
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          placeholder="Staff email" className="w-full px-4 py-3 mb-3 bg-white outline-none"
          style={inputStyle} data-testid="staff-login-email"
        />
        <input
          type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
          placeholder="Password" className="w-full px-4 py-3 bg-white outline-none"
          style={inputStyle} data-testid="staff-login-password"
        />
        {error && <p className="text-sm mt-4 m-0" style={{ color: "var(--iw-accent-red)" }} data-testid="staff-login-error">{error}</p>}
        <button type="submit" className="iw-btn-primary w-full mt-6 justify-center" disabled={busy} data-testid="staff-login-submit">
          {busy ? "Signing in…" : "Sign in"} <span aria-hidden="true">↗</span>
        </button>
      </form>
    </div>
  );
}

function MemberAdmin({ principal, onLogout }) {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ healthie_id: "", email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/members`);
      setMembers(data.members || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await axios.post(`${API}/admin/members`, {
        healthie_id: form.healthie_id.trim(),
        email: form.email.trim(),
        name: form.name.trim(),
      });
      setNotice(`Member created — send them ID "${form.healthie_id.trim()}" + their email to sign in.`);
      setForm({ healthie_id: "", email: "", name: "" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not create member.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name}? They will lose the ability to sign in.`)) return;
    try {
      await axios.delete(`${API}/admin/members/${id}`);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen p-4 sm:p-8" style={{ background: "var(--iw-bg)" }} data-testid="admin-panel">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="iw-mono uppercase" style={{ color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }}>
              Staff Console
            </div>
            <h1 className="serif font-bold m-0" style={{ color: "var(--iw-primary)", fontSize: "28px" }}>Members</h1>
            <p className="text-sm m-0 mt-1" style={{ color: "var(--iw-muted)" }}>Signed in as {principal.email}</p>
          </div>
          <button className="iw-btn-ghost" onClick={onLogout} data-testid="staff-logout-btn" style={{ padding: "0.6rem 1rem", fontSize: "12px" }}>
            Sign out
          </button>
        </div>

        <form onSubmit={create} className="iw-card p-6 mb-8" data-testid="admin-create-member-form">
          <h2 className="serif font-bold m-0 mb-4" style={{ color: "var(--iw-primary)", fontSize: "18px" }}>Create a member</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <input placeholder="Healthie ID" value={form.healthie_id} required
              onChange={(e) => setForm((f) => ({ ...f, healthie_id: e.target.value }))}
              className="px-3 py-2.5 bg-white outline-none" style={inputStyle} data-testid="admin-member-healthie" />
            <input placeholder="Email" type="email" value={form.email} required
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="px-3 py-2.5 bg-white outline-none" style={inputStyle} data-testid="admin-member-email" />
            <input placeholder="Full name" value={form.name} required
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="px-3 py-2.5 bg-white outline-none" style={inputStyle} data-testid="admin-member-name" />
          </div>
          {error && <p className="text-sm mt-3 m-0" style={{ color: "var(--iw-accent-red)" }} data-testid="admin-error">{error}</p>}
          {notice && <p className="text-sm mt-3 m-0" style={{ color: "var(--iw-primary)" }} data-testid="admin-notice">{notice}</p>}
          <button type="submit" className="iw-btn-primary mt-4" disabled={busy} data-testid="admin-create-submit">
            {busy ? "Creating…" : "Add member"} <span aria-hidden="true">↗</span>
          </button>
        </form>

        <div className="iw-card p-6" data-testid="admin-member-list">
          <h2 className="serif font-bold m-0 mb-4" style={{ color: "var(--iw-primary)", fontSize: "18px" }}>
            {members.length} member{members.length === 1 ? "" : "s"}
          </h2>
          {members.length === 0 && <p className="text-sm m-0" style={{ color: "var(--iw-muted)" }}>No members yet.</p>}
          <div className="flex flex-col divide-y" style={{ borderColor: "var(--iw-border-soft)" }}>
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between py-3 gap-3 flex-wrap" data-testid={`admin-member-row-${m.healthie_id}`}>
                <div>
                  <div className="font-semibold" style={{ color: "var(--iw-primary)" }}>{m.name}</div>
                  <div className="iw-mono text-xs" style={{ color: "var(--iw-muted)" }}>{m.healthie_id} · {m.email}</div>
                </div>
                <button className="iw-btn-ghost" style={{ padding: "0.45rem 0.8rem", fontSize: "11px" }}
                  onClick={() => remove(m.id, m.name)} data-testid={`admin-member-delete-${m.healthie_id}`}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <AccessCodes />
        <ChangePassword />
        <BackfillLinks />
      </div>
    </div>
  );
}

// Payment-confirmed access codes: 12 sessions each, one per week.
function AccessCodes() {
  const [codes, setCodes] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [latest, setLatest] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/codes`);
      setCodes(data.codes || []);
    } catch (e) {
      console.error("Codes load failed", e);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const { data } = await axios.post(`${API}/admin/codes`, { note: note.trim() || null });
      setLatest(data.code);
      setNote("");
      load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Could not create code.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (c) => {
    if (!window.confirm(`Revoke code ${c.code}? The client won't be able to redeem it.`)) return;
    try {
      await axios.delete(`${API}/admin/codes/${c.id}`);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const statusColor = (s) => {
    if (s === "redeemed") return "var(--iw-muted)";
    if (s === "revoked") return "var(--iw-accent-red)";
    return "var(--iw-primary)";
  };

  return (
    <div className="iw-card p-6 mt-8" data-testid="admin-access-codes">
      <h2 className="serif font-bold m-0 mb-1" style={{ color: "var(--iw-primary)", fontSize: "18px" }}>Access codes</h2>
      <p className="text-sm m-0 mb-4" style={{ color: "var(--iw-muted)" }}>
        Generate a code after payment is confirmed. Each code = 12 sessions, one per week. Give the code to the client — they redeem it on their My Progress page.
      </p>
      <form onSubmit={create} className="flex gap-3 flex-wrap">
        <input
          placeholder="Note (client name / invoice #, optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="px-3 py-2.5 bg-white outline-none flex-1 min-w-[220px]"
          style={inputStyle}
          data-testid="admin-code-note"
        />
        <button type="submit" className="iw-btn-primary" disabled={busy} data-testid="admin-code-create-btn">
          {busy ? "Generating…" : "Generate code"} <span aria-hidden="true">↗</span>
        </button>
      </form>
      {err && <p className="text-sm mt-3 m-0" style={{ color: "var(--iw-accent-red)" }} data-testid="admin-code-error">{err}</p>}
      {latest && (
        <p className="text-sm mt-3 m-0" style={{ color: "var(--iw-primary)" }} data-testid="admin-code-latest">
          New code: <strong className="iw-mono tracking-widest">{latest}</strong> — copy it and send it to the client.
        </p>
      )}
      {codes.length > 0 && (
        <div className="flex flex-col divide-y mt-5" style={{ borderColor: "var(--iw-border-soft)" }} data-testid="admin-code-list">
          {codes.map((c) => (
            <div key={c.id} className="flex items-center justify-between py-2.5 gap-3 flex-wrap" data-testid={`admin-code-row-${c.code}`}>
              <div>
                <span className="iw-mono tracking-widest font-semibold" style={{ color: "var(--iw-primary)" }}>{c.code}</span>
                <span className="iw-mono text-xs ml-3" style={{ color: statusColor(c.status) }}>
                  {c.status}{c.redeemed_by_name ? ` · ${c.redeemed_by_name}` : ""}{c.note ? ` · ${c.note}` : ""}
                </span>
              </div>
              {c.status === "active" && (
                <button className="iw-btn-ghost" style={{ padding: "0.4rem 0.75rem", fontSize: "11px" }}
                  onClick={() => revoke(c)} data-testid={`admin-code-revoke-${c.code}`}>
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BackfillLinks() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    if (!window.confirm("Re-link existing session rows in Notion to member records? This is safe to run anytime.")) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/backfill-links`);
      setResult(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Backfill failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="iw-card p-6 mt-8" data-testid="admin-backfill">
      <h2 className="serif font-bold m-0 mb-1" style={{ color: "var(--iw-primary)", fontSize: "18px" }}>Backfill Notion links</h2>
      <p className="text-sm m-0 mb-4" style={{ color: "var(--iw-muted)" }}>
        Links existing NSOE session rows to their member record (matched by Healthie ID). Safe to re-run.
      </p>
      {err && <p className="text-sm mb-3 m-0" style={{ color: "var(--iw-accent-red)" }} data-testid="admin-backfill-error">{err}</p>}
      {result && (
        <p className="text-sm mb-3 m-0" style={{ color: "var(--iw-primary)" }} data-testid="admin-backfill-result">
          Scanned {result.scanned} · linked {result.linked} · already linked {result.already} · no match {result.no_match} · no Healthie ID {result.no_healthie}
          {result.errors ? ` · errors ${result.errors}` : ""}
        </p>
      )}
      <button className="iw-btn-primary" onClick={run} disabled={busy} data-testid="admin-backfill-btn">
        {busy ? "Linking…" : "Run backfill"} <span aria-hidden="true">↗</span>
      </button>
    </div>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null); setErr(null);
    try {
      await axios.post(`${API}/auth/staff/change-password`, {
        current_password: current, new_password: next,
      });
      setMsg("Password updated.");
      setCurrent(""); setNext("");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Could not update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="iw-card p-6 mt-8" data-testid="admin-change-password-form">
      <h2 className="serif font-bold m-0 mb-1" style={{ color: "var(--iw-primary)", fontSize: "18px" }}>Change your password</h2>
      <p className="text-sm m-0 mb-4" style={{ color: "var(--iw-muted)" }}>Minimum 8 characters. Takes effect immediately.</p>
      <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
        <input type="password" placeholder="Current password" value={current} required
          onChange={(e) => setCurrent(e.target.value)} className="px-3 py-2.5 bg-white outline-none" style={inputStyle} data-testid="admin-current-password" />
        <input type="password" placeholder="New password" value={next} required minLength={8}
          onChange={(e) => setNext(e.target.value)} className="px-3 py-2.5 bg-white outline-none" style={inputStyle} data-testid="admin-new-password" />
      </div>
      {err && <p className="text-sm mt-3 m-0" style={{ color: "var(--iw-accent-red)" }} data-testid="admin-password-error">{err}</p>}
      {msg && <p className="text-sm mt-3 m-0" style={{ color: "var(--iw-primary)" }} data-testid="admin-password-notice">{msg}</p>}
      <button type="submit" className="iw-btn-primary mt-4" disabled={busy} data-testid="admin-change-password-submit">
        {busy ? "Updating…" : "Update password"} <span aria-hidden="true">↗</span>
      </button>
    </form>
  );
}

function CenterMsg({ text }) {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--iw-bg)", color: "var(--iw-muted)" }}>
      {text}
    </div>
  );
}

const inputStyle = {
  border: "1px solid var(--iw-border)",
  borderRadius: "2px",
  color: "var(--iw-primary)",
  fontFamily: "'IBM Plex Sans', sans-serif",
};
